import { useState, useEffect } from "react";
import _ from "lodash";

// Define the props interface for image URLs (all optional)
interface UseTourImageFallbackProps {
    productImagePath?: string;
    productSmallToMediumSizeImagePath?: string;
    productLargeSizeImagePath?: string;
}

// Custom React hook to select the best available image URL with fallback
const useTourImageFallback = ({
    productImagePath,
    productSmallToMediumSizeImagePath,
    productLargeSizeImagePath,
}: UseTourImageFallbackProps): string => {
    // Default image path to use when no valid image is found
    const placeholder = "/images/default-tour.png";

    // Trim whitespace from each URL input and assign defaults if undefined
    const largeUrl = _.trim(productLargeSizeImagePath || ""); // Highest priority
    const mediumUrl = _.trim(productSmallToMediumSizeImagePath || "");
    const fallbackUrl = _.trim(productImagePath || "");

    // Medium gets priority over fallback if both exist
    const smallMediumUrl = mediumUrl || fallbackUrl;

    // Local state to store the selected image URL
    const [imageUrl, setImageUrl] = useState<string>(
        largeUrl || smallMediumUrl || placeholder, // Initial best guess
    );

    // React effect to validate image URLs when dependencies change
    useEffect(() => {
        // Flag to prevent state updates if component is unmounted
        let isCancelled = false;

        // Helper to check if a given image URL loads successfully
        const doesImageExist = (url: string): Promise<boolean> =>
            new Promise((resolve) => {
                if (!url) return resolve(false); // Skip if empty
                const img = new Image(); // Create a new image element
                img.onload = () => resolve(true); // Resolve true on successful load
                img.onerror = () => resolve(false); // Resolve false on error
                img.src = url; // Start loading
            });

        // Asynchronously try each URL in order and use the first one that loads
        const loadBestImage = async () => {
            const tryUrls = [largeUrl, smallMediumUrl, placeholder]; // Priority list
            const results = await Promise.all(tryUrls.map((url) => doesImageExist(url))); // Check all in parallel
            const foundIndex = results.findIndex(Boolean); // Find first successful image

            if (!isCancelled && foundIndex !== -1) {
                setImageUrl(tryUrls[foundIndex]); // Set the image URL if valid and still mounted
            }
        };

        // Set the current best guess immediately (while async check runs)
        setImageUrl(largeUrl || smallMediumUrl || placeholder);

        // Start checking the image URLs
        loadBestImage();

        // Cleanup function to prevent updating state on unmounted component
        return () => {
            isCancelled = true;
        };
    }, [largeUrl, smallMediumUrl]); // Rerun the effect when these URLs change

    // Return the best valid image URL for use in the component
    return imageUrl;
};

export default useTourImageFallback;
